import { FC, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
  AMOUNT_INPUT_MAX_LENGTH,
  DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL,
  EMPTY_VALUE_DASH,
  FORM_ERROR_TIMEOUT_SHORT,
} from "../../helpers/constants/common";
import { REGEX_ALL_ZERO } from "../../helpers/constants/validations";
import {
  checkPercentageRangeError,
  removeSpecialWithPrefix,
} from "../../helpers/utils";
import { MuiTextField, MuiTextFieldProps } from "../textField/MuiTextfield";
import { getFormattedValue, valueWithoutFormat } from "./CurrencyUtils";

export type TextFieldCurrencyProps = MuiTextFieldProps & {
  formId: string;
  locale: string;
  isCurrencyChanged?: string;
  formatErrorMessage: string;
  zeroValidationMessage: string;
  getFinalValues?: (value: string, valueNonFormatted: string) => void;
  isLabelInvisible?: boolean;
  useGrouping?: boolean;
  onFocusCalled?: () => void;
  allowDecimal?: boolean;
  isForPercentageUsage?: boolean;
  percentRangeErrorMessage?: string;
  showDoubleDash?: boolean;
  ignoreZeroValidation?: boolean;
  defaultValue?: string;
};
export const TextFieldCurrency: FC<TextFieldCurrencyProps> = (
  props: TextFieldCurrencyProps,
) => {
  const { setError, setValue, clearErrors } = useFormContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastValidValue = useRef<string>("");

  const {
    formId,
    isCurrencyChanged,
    label,
    isMandatory,
    locale,
    formatErrorMessage,
    zeroValidationMessage,
    ignoreZeroValidation = false,
    getFinalValues,
    isLabelInvisible,
    useGrouping = true,
    onFocusCalled,
    allowDecimal = true,
    isForPercentageUsage,
    percentRangeErrorMessage = "",
    requiredARIALabel,
    showDoubleDash,
    defaultValue = DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL,
    ...rest
  } = props;

  const triggerOnInputManually = () => {
    if (inputRef.current) {
      inputRef.current.dispatchEvent(
        new Event("input", {
          bubbles: true,
        }),
      );
    }
  };

  useEffect(() => {
    if (inputRef.current && showDoubleDash) {
      inputRef.current.value = EMPTY_VALUE_DASH;
    }
  }, [showDoubleDash]);

  useEffect(() => {
    triggerOnInputManually();
  }, [isCurrencyChanged]);

  const validateInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow Ctrl+V for paste functionality
    if ((e.ctrlKey && e.key === "v") || (e.ctrlKey && e.key === "a")) {
      return;
    }

    // Allow only number keys, control keys, and decimal points
    if (
      !/\d|Backspace|ArrowLeft|ArrowRight|Delete|Control|Tab|Enter/.test(e.key)
    ) {
      setError(formId, {
        type: "custom",
        message: formatErrorMessage,
      });
      e.preventDefault(); // Prevent non-numeric and non-control keys
    }
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (showDoubleDash) {
      target.value = EMPTY_VALUE_DASH;
      return;
    }

    const number = parseInt(target.value.replace(/\D/g, ""), 10);

    if (number.toString().length > AMOUNT_INPUT_MAX_LENGTH) {
      target.value = lastValidValue.current;
      return;
    }

    const formattedValue = getFormattedValue(
      number,
      locale,
      allowDecimal,
      useGrouping,
    );

    target.value = formattedValue;
    lastValidValue.current = formattedValue;
    clearErrors(formId);
    getFinalValues?.(target.value, valueWithoutFormat(target.value));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    clearErrors(formId);
    if (showDoubleDash) {
      target.value = EMPTY_VALUE_DASH;
      return;
    }

    const regZeroCheck = REGEX_ALL_ZERO;
    regZeroCheck.lastIndex = 0;
    const regZeroTest = regZeroCheck.test(
      removeSpecialWithPrefix(target.value),
    );
    if (regZeroTest && !ignoreZeroValidation) {
      setTimeout(() => {
        setError(formId, {
          type: "custom",
          message: zeroValidationMessage,
        });
      }, FORM_ERROR_TIMEOUT_SHORT);
    }
    if (isForPercentageUsage) {
      if (checkPercentageRangeError(target.value)) {
        setTimeout(() => {
          setError(formId, {
            type: "custom",
            message: percentRangeErrorMessage,
          });
        }, FORM_ERROR_TIMEOUT_SHORT);
      }
    }
    props?.onBlur && props.onBlur(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setValue(formId, e.target.value);
    if (onFocusCalled) {
      onFocusCalled();
    }
  };

  const inputProps = {
    onBlur: handleBlur,
    onInput: handleInput,
    onKeyDown: validateInput,
    onFocus: handleFocus,
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasted = e.clipboardData.getData("text");

    // Convert pasted value to number handling US and European formats
    let numericValue: number;
    if (
      pasted.includes(",") &&
      pasted.lastIndexOf(".") > pasted.lastIndexOf(",")
    ) {
      // US format: 1,234.56
      numericValue = parseFloat(pasted.replace(/,/g, ""));
    } else if (
      pasted.includes(".") &&
      pasted.lastIndexOf(",") > pasted.lastIndexOf(".")
    ) {
      // European format: 1.234,56
      numericValue = parseFloat(pasted.replace(/\./g, "").replace(",", "."));
    } else {
      // Simple number or other format
      numericValue = parseFloat(pasted.replace(/[^\d.-]/g, ""));
    }
    // Floor to 2 decimal places
    if (!isNaN(numericValue)) {
      numericValue = Math.floor(numericValue * 100) / 100;
    }

    const pastedNumber = isNaN(numericValue)
      ? parseFloat(defaultValue)
      : numericValue;

    // If no decimal point, append "00" to create decimal value
    const valueToProcess = `${pastedNumber * 100}`;

    // Remove all non-digit characters except decimal point
    const sanitized = valueToProcess.replace(/[^\d.]/g, "");

    let formattedValue = getFormattedValue(
      Number(
        sanitized.toString().length > AMOUNT_INPUT_MAX_LENGTH
          ? sanitized.substring(0, AMOUNT_INPUT_MAX_LENGTH)
          : sanitized,
      ),
      locale,
      allowDecimal,
      useGrouping,
    );

    rest.onChange?.({
      target: { value: formattedValue },
    } as React.ChangeEvent<HTMLInputElement>);
    getFinalValues?.(formattedValue, valueWithoutFormat(formattedValue));
  };
  return (
    <MuiTextField
      inputRef={inputRef}
      isMandatory={isMandatory}
      label={label}
      isLabelInvisible={isLabelInvisible}
      {...rest}
      {...inputProps}
      requiredARIALabel={requiredARIALabel}
      onPaste={handlePaste}
    />
  );
};
